import axios from 'axios';
import authService from './authService';

// Base API configuration
const API_BASE_URL = 'http://localhost:8080/api';

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      authService.logout();
    }
    return Promise.reject(error);
  }
);

// Backend Service Class
class BackendService {
  // User Management APIs
  async getUserProfile() {
    try {
      const response = await apiClient.get('/users/profile');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get user profile' };
    }
  }

  async updateUserProfile(userData) {
    try {
      const response = await apiClient.put('/users/profile', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update profile' };
    }
  }

  async getAllUsers() {
    try {
      const response = await apiClient.get('/users');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get users' };
    }
  }

  // AI Agents APIs
  async getUserAgents() {
    try {
      const response = await apiClient.get('/ai-agents');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get AI agents' };
    }
  }

  async createAgent(agentData) {
    try {
      const response = await apiClient.post('/ai-agents', agentData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create AI agent' };
    }
  }

  async updateAgent(id, agentData) {
    try {
      const response = await apiClient.put(`/ai-agents/${id}`, agentData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update AI agent' };
    }
  }

  async deleteAgent(id) {
    try {
      const response = await apiClient.delete(`/ai-agents/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete AI agent' };
    }
  }

  // Workflows APIs
  async getUserWorkflows() {
    try {
      const response = await apiClient.get('/workflows');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get workflows' };
    }
  }

  async createWorkflow(workflowData) {
    try {
      const response = await apiClient.post('/workflows', workflowData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create workflow' };
    }
  }

  async updateWorkflow(id, workflowData) {
    try {
      const response = await apiClient.put(`/workflows/${id}`, workflowData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to update workflow' };
    }
  }

  async executeWorkflow(id) {
    try {
      const response = await apiClient.post(`/workflows/${id}/execute`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to execute workflow' };
    }
  }

  async deleteWorkflow(id) {
    try {
      const response = await apiClient.delete(`/workflows/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete workflow' };
    }
  }

  // Services APIs
  async getPublicServices() {
    try {
      const response = await apiClient.get('/services/public');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get services' };
    }
  }

  async getServiceById(id) {
    try {
      const response = await apiClient.get(`/services/public/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get service' };
    }
  }

  async deployService(id) {
    try {
      const response = await apiClient.post(`/services/${id}/deploy`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to deploy service' };
    }
  }

  // Dashboard APIs
  async getUserStats() {
    try {
      const response = await apiClient.get('/dashboard/stats');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get user stats' };
    }
  }

  async getAdminStats() {
    try {
      const response = await apiClient.get('/dashboard/admin/stats');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get admin stats' };
    }
  }

  // API Keys APIs
  async getUserApiKeys() {
    try {
      const response = await apiClient.get('/api-keys');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to get API keys' };
    }
  }

  async createApiKey(keyData) {
    try {
      const response = await apiClient.post('/api-keys', keyData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to create API key' };
    }
  }

  async deleteApiKey(id) {
    try {
      const response = await apiClient.delete(`/api-keys/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete API key' };
    }
  }

  // Test API connection
  async testConnection() {
    try {
      const response = await apiClient.get('/test');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Connection test failed' };
    }
  }
}

// Create and export a single instance
const backendService = new BackendService();
export default backendService;